import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import PropTypes from 'prop-types';

import Button from '../../Shared/Button/Button';

/**
 * A confirmation modal that appears when a user tries to remove a provider from the roster.
 * @param {{
 *  isOpen: boolean,
 *  onClose: () => void,
 *  onConfirm: () => void,
 *  providerName: string
 * }} props
 */
const RemoveProviderModal = ({ isOpen, onClose, onConfirm, providerName }) => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        {/* Modal container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-md transform rounded-2xl bg-white p-8 text-center shadow-xl transition-all"
                data-testid="remove-provider-modal"
              >
                <Dialog.Title
                  as="h3"
                  className="text-xl font-semibold leading-7 mb-4"
                >
                  Remove Provider from Roster
                </Dialog.Title>

                <Dialog.Description className="text-base mb-8 font-extralight">
                  Are you sure you want to delete
                  <br />
                  {providerName} from your roster?
                </Dialog.Description>

                <div className="flex gap-4 justify-center">
                  <Button
                    variant="outline"
                    theme="deep-blue"
                    onClick={onClose}
                    className="px-8"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    theme="deep-blue"
                    onClick={handleConfirm}
                    className="px-8"
                  >
                    Delete
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

RemoveProviderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  providerName: PropTypes.string.isRequired,
};

export default RemoveProviderModal;
